import { DOCUMENT, NgClass } from '@angular/common';
import {
  AfterContentInit,
  ContentChildren,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges
} from '@angular/core';

import { startWith, tap } from 'rxjs';

import { CssUtils } from '@hdms/shared/util-core';

import { SvgIconComponent } from '../svg-icon/svg-icon.component';

const NAMESPACE = 'btn';
const SVG_ICON_TYPE = 'SHRD-UIC-SVG-ICON';
const COMMENT_TYPE = '#comment';
const SM_BREAKPOINT_HIDDEN_CLASSES = ['d-none d-md-inline', 'visually-hidden-sm-down'];

export const ButtonClassName = {
  BUTTON: NAMESPACE,
  HAS_ICON_BEFORE: 'has-icon-before',
  HAS_ICON_AFTER: 'has-icon-after',
  HAS_ICON_BEFORE_AND_AFTER: 'has-icon-before-and-after',
  HAS_ICON_ONLY: 'has-icon-only',
  HAS_ICON_ONLY_SM_DOWN: 'has-icon-only-sm-down',
  SELECTED: 'selected'
};

export const ButtonBaseClasses = {
  primary: 'primary',
  secondary: 'secondary',
  secondaryDark: 'secondary-dark',
  tertiary: 'tertiary',
  tertiaryMajor: 'tertiary-major',
  tertiaryMinor: 'tertiary-minor',
  tertiaryMinorBorderless: 'tertiary-minor-borderless',
  tertiaryMinorBorderlessNeutralDark: 'tertiary-minor-borderless-neutraldark',
  tertiaryTrivialTextBold: 'tertiary-trivial-text-bold',
  tertiaryFlyout: 'tertiary-flyout',
  tertiaryOption: 'tertiary-option',
  link: 'link',
  headerNav: 'header-nav',
  navList: 'nav-list'
} as const;

const IconClasses = {
  ICON_BEFORE: 'icon-before',
  ICON_AFTER: 'icon-after'
};

export type ButtonBaseClassesType = keyof typeof ButtonBaseClasses;

const buttonDefaultVariant = ButtonBaseClasses.primary;

/**
 * `ButtonVariant` should be used for applying `variant`/`swapVariant` input binding values.
 * The existing implementation already relies on applying the keys (!!) of `ButtonBaseClasses`,
 * so it's not possible to change the existing approach without huge refactoring.
 *
 * !!! PLEASE ALWAYS MAINTAIN THE ENUM WHEN ADDING A NEW VARIANT !!!
 */
export enum ButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  SECONDARY_DARK = 'secondaryDark',
  TERTIARY = 'tertiary',
  TERTIARY_MAJOR = 'tertiaryMajor',
  TERTIARY_MINOR = 'tertiaryMinor',
  TERTIARY_MINOR_BORDERLESS = 'tertiaryMinorBorderless',
  TERTIARY_MINOR_BORDERLESS_NEUTRAL_DARK = 'tertiaryMinorBorderlessNeutralDark',
  TERTIARY_TRIVIAL_TEXT_BOLD = 'tertiaryTrivialTextBold',
  TERTIARY_FLYOUT = 'tertiaryFlyout',
  TERTIARY_OPTION = 'tertiaryOption',
  LINK = 'link',
  HEADER_NAV = 'headerNav',
  NAV_LIST = 'navList'
}

@Directive({
  selector: '[shrdUicButton]',
  exportAs: 'buttonDirective'
})
export class ButtonDirective implements AfterContentInit, OnChanges {
  public isSelected = false;

  @Input() swapVariant?: ButtonBaseClassesType;
  @Input() ngClass?: NgClass['ngClass'];
  @Input() class?: string;
  @Input() form?: string;

  @HostBinding('class.btn') @Input() variant: ButtonBaseClassesType = buttonDefaultVariant;
  @HostBinding('class') private get hostClass() {
    return this.variant ? this.hostClasses : CssUtils.getJoinedClassList(this.ngClass, this.class);
  }

  @ContentChildren(SvgIconComponent, { read: ElementRef, descendants: true })
  private readonly svgIcons: QueryList<ElementRef<HTMLElement>>;

  private readonly element: HTMLElement;
  private hostClasses: string;
  private iconClassNames: string[] = [];

  constructor(@Inject(DOCUMENT) private readonly document: Document, { nativeElement }: ElementRef) {
    this.element = nativeElement;
  }

  ngAfterContentInit(): void {
    this.svgIcons.changes
      .pipe(startWith(this.svgIcons), tap(this.setIconClasses.bind(this)))
      .subscribe(this.applyHostClasses.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.variant && !changes.variant.firstChange) {
      this.applyHostClasses();
    }
  }

  private applyHostClasses(): void {
    this.hostClasses = CssUtils.getJoinedClassList(
      `${NAMESPACE}-${ButtonBaseClasses[this.variant] || buttonDefaultVariant}`,
      this.isSelected && ButtonClassName.SELECTED,
      this.iconClassNames,
      this.ngClass,
      this.class
    );
  }

  private setIconClasses(): void {
    if (!this.svgIcons.length) {
      return;
    }

    const classNames = [];

    if (this.hasIconsOnly()) {
      this.addClassIfNotIncluded(classNames, ButtonClassName.HAS_ICON_ONLY);
    } else {
      const childNodes = this.getRealChildNodes();

      const iconBeforeIndex = childNodes.findIndex(node => node === this.svgIcons.first.nativeElement);
      const iconAfterIndex = childNodes.findIndex(node => node === this.svgIcons.last.nativeElement);
      const hasIconBefore = iconBeforeIndex === 0;
      const hasIconAfter = iconAfterIndex === childNodes.length - 1;

      if (hasIconBefore && !hasIconAfter) {
        (childNodes[iconBeforeIndex] as HTMLElement).className += ` ${IconClasses.ICON_BEFORE}`;
        classNames.push(ButtonClassName.HAS_ICON_BEFORE);
      } else if (!hasIconBefore && hasIconAfter) {
        (childNodes[iconAfterIndex] as HTMLElement).className += ` ${IconClasses.ICON_AFTER}`;
        classNames.push(ButtonClassName.HAS_ICON_AFTER);
      } else {
        (childNodes[iconBeforeIndex] as HTMLElement).className += ` ${IconClasses.ICON_BEFORE}`;
        (childNodes[iconAfterIndex] as HTMLElement).className += ` ${IconClasses.ICON_AFTER}`;
        classNames.push(ButtonClassName.HAS_ICON_BEFORE_AND_AFTER);
      }

      if ((hasIconBefore || hasIconAfter) && this.hasIconsOnlySmDown(childNodes)) {
        this.addClassIfNotIncluded(classNames, ButtonClassName.HAS_ICON_ONLY_SM_DOWN);
      }
    }

    this.iconClassNames = classNames;
  }

  private addClassIfNotIncluded(classNames: string[], classToAdd: string): void {
    !this.class?.includes(classToAdd) && classNames.push(classToAdd);
  }

  /**
   * Return only "real" child nodes by filtering out comments
   * @private
   */
  private getRealChildNodes(): ChildNode[] {
    // take the parent from first svg icon as it could be
    // svg icons are nested inside an additional span
    const parent = this.svgIcons.first.nativeElement.parentElement;
    return Array.from(parent.childNodes).filter(node => node.nodeName !== COMMENT_TYPE);
  }

  private hasIconsOnly(): boolean {
    return !!this.svgIcons.length && this.getRealChildNodes().every(node => node.nodeName === SVG_ICON_TYPE);
  }

  private hasIconsOnlySmDown(nodes: ChildNode[]): boolean {
    const nonSvgIconNodes = nodes.filter(node => node.nodeName !== SVG_ICON_TYPE);
    return nonSvgIconNodes.every(node => this.hasSmHiddenBreakpointClassName(node));
  }

  private hasSmHiddenBreakpointClassName(node: ChildNode): boolean {
    if (node instanceof Element) {
      const nodeClassName = node.className;
      return SM_BREAKPOINT_HIDDEN_CLASSES.some(smClassName =>
        smClassName.split(' ').every(requiredSmHiddenClass => nodeClassName.includes(requiredSmHiddenClass))
      );
    }

    return false;
  }

  @HostListener('click')
  private onClick(): void {
    this.swapSelectedVariant();
  }

  private swapSelectedVariant(): void {
    if (this.variant && this.swapVariant) {
      [this.variant, this.swapVariant] = [this.swapVariant, this.variant];
    }
    this.isSelected = !this.isSelected;

    this.applyHostClasses();
  }
}
